import React, { useState, useEffect } from "react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import { Package, Truck, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackingModal, setTrackingModal] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/orders/my-orders")
        .then((res) => setOrders(res.data))
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleOpenTracking = async (trackingCode) => {
    try {
      const res = await api.get(`/shipping/tracking/${trackingCode}`);
      setTrackingModal(res.data);
    } catch (err) {
      toast.error("Não foi possível carregar o rastreamento.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col justify-between">
      <div>
        <Header />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-8 border-b border-white/10 mb-8">
            <div>
              <span className="text-xs tracking-widest text-[#FF8C69] uppercase font-bold">
                Painel do Cliente
              </span>
              <h1 className="font-heading text-3xl font-bold mt-1 text-white">
                Meus Pedidos & Rastreamento
              </h1>
            </div>

            <div className="flex items-center gap-2 text-xs text-[#A1A1AA]">
              <Package className="w-4 h-4 text-[#FF8C69]" />
              <span>{orders.length} {orders.length === 1 ? "pedido encontrado" : "pedidos encontrados"}</span>
            </div>
          </div>

          {!user ? (
            <div className="py-20 text-center rounded-3xl bg-[#121214] border border-white/10 p-8 space-y-4">
              <Package className="w-12 h-12 text-[#A1A1AA] mx-auto opacity-40" />
              <p className="font-heading text-xl text-white">Identificação Necessária</p>
              <p className="text-xs text-[#A1A1AA] max-w-sm mx-auto">
                Faça login para acompanhar o status e o código de envio dos seus pedidos.
              </p>
            </div>
          ) : loading ? (
            <div className="space-y-4">
              {[1, 2].map((n) => (
                <div key={n} className="h-40 rounded-2xl bg-[#121214] border border-white/10 animate-pulse" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="py-20 text-center rounded-3xl bg-[#121214] border border-white/10 p-8 space-y-4">
              <Package className="w-12 h-12 text-[#A1A1AA] mx-auto opacity-40" />
              <p className="font-heading text-xl text-white">Nenhum pedido realizado ainda.</p>
              <p className="text-xs text-[#A1A1AA]">Quando você fizer uma compra, ela aparecerá aqui.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div
                  key={order.order_id}
                  data-testid={`order-card-${order.order_id}`}
                  className="rounded-3xl bg-[#121214] border border-white/10 p-6 sm:p-8 space-y-6 shadow-xl"
                >
                  
                  {/* Order Top Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-white/10">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-heading font-bold text-lg text-white">
                          #{order.public_order_number}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                            order.payment_status === "paid"
                              ? "bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30"
                              : order.payment_status === "refunded"
                              ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                              : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                          }`}
                        >
                          {order.payment_status === "paid"
                            ? "Pago"
                            : order.payment_status === "refunded"
                            ? "Reembolsado"
                            : "Aguardando Pagamento"}
                        </span>
                      </div>
                      <p className="text-[11px] text-[#A1A1AA] mt-1">
                        Data: {new Date(order.created_at).toLocaleDateString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>

                    <div className="text-right">
                      <span className="font-heading text-xl font-bold text-[#FF8C69]">
                        {order.formatted_total}
                      </span>
                      <p className="text-[11px] text-[#A1A1AA]">
                        {order.shipping_service} • {order.payment_method === "pix" ? "Pix Instantâneo" : "Cartão de Crédito"}
                      </p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex gap-3 p-3 rounded-2xl bg-[#18181B] border border-white/10">
                        <img
                          src={item.main_media_url}
                          alt={item.name}
                          className="w-14 h-16 object-cover rounded-xl bg-[#121214]"
                        />
                        <div className="flex-1 min-w-0 flex flex-col justify-between text-xs">
                          <div>
                            <p className="font-bold text-white truncate">{item.name}</p>
                            <p className="text-[11px] text-[#A1A1AA] font-mono">SKU: {item.sku}</p>
                          </div>
                          <div className="flex justify-between items-center text-[#FF8C69] font-heading font-bold">
                            <span>Qtd: {item.quantity}</span>
                            <span>{item.formatted_price}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Tracking Action */}
                  <div className="pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                      <Truck className="w-4 h-4 text-[#FF8C69]" />
                      <span>Rastreamento Correios: <strong className="text-white font-mono">{order.tracking_code}</strong></span>
                    </div>

                    <button
                      onClick={() => handleOpenTracking(order.tracking_code)}
                      data-testid={`btn-track-order-${order.order_id}`}
                      className="px-4 py-2 rounded-xl bg-[#18181B] border border-white/10 hover:border-[#FF8C69] text-xs font-bold text-[#FF8C69] hover:text-white transition-colors flex items-center gap-2"
                    >
                      <span>Ver Rastreio</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </div>

                </div>
              ))}
            </div>
          )}

        </main>
      </div>

      {/* Tracking Modal */}
      {trackingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="w-full max-w-lg rounded-3xl bg-[#121214] border border-white/10 shadow-2xl p-6 sm:p-8 text-white space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6 text-[#FF8C69]" />
                <div>
                  <h3 className="font-heading font-bold text-lg">Rastreamento de Objeto</h3>
                  <p className="font-mono text-xs text-[#FF8C69]">{trackingModal.tracking_code}</p>
                </div>
              </div>
              <button
                onClick={() => setTrackingModal(null)}
                className="text-[#A1A1AA] hover:text-white p-1.5"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="p-3 rounded-xl bg-[#18181B] border border-white/10 flex justify-between">
                <span className="text-[#A1A1AA]">Transportadora:</span>
                <strong className="text-white">{trackingModal.carrier} ({trackingModal.service})</strong>
              </div>

              {/* Timeline */}
              <div className="space-y-3 pt-2">
                <p className="font-bold text-white">Histórico de Movimentações:</p>
                {trackingModal.events?.map((ev, i) => (
                  <div key={i} className="flex gap-3 text-xs pl-2 border-l-2 border-[#FF8C69]">
                    <div>
                      <p className="font-bold text-[#FF8C69]">{ev.status}</p>
                      <p className="text-[11px] text-[#A1A1AA]">{ev.location} • {new Date(ev.date).toLocaleString("pt-BR")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setTrackingModal(null)}
              className="w-full py-3 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-xs font-bold text-[#0A0A0B]"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
